import React, { useState, useEffect } from 'react';
import { usePageTracking } from '../../../hooks/usePageTracking';
import {
  checkDomainAge,
  getPDFStatus,
  generatePDF,
  downloadPDFFromS3,
  getPdfSubmissionPlatforms,
  completeSubmission,
  getPDFLibrary,
  type PDFGenerationResult,
  type PDFStatusData,
} from '../../../services/pdfSubmissionService';
import {
  initiatePdfSubmissionScan,
  getPdfSubmissionScanStatus,
} from '../../../services/discoveryService';
import { updatePDFMetadata } from '../../../services/pdfSubmissionService';

interface PDFSubmissionPageProps {
  onBack: () => void;
  darkMode: boolean;
  userId: string;
}

const PLATFORM_COLORS: Record<string, string> = {
  'slideshare': 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-orange-100 dark:border-orange-800',
  'scribd': 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800',
  'issuu': 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 border-purple-100 dark:border-purple-800',
  'mediafire': 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-400 border-cyan-100 dark:border-cyan-800',
  '4shared': 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border-indigo-100 dark:border-indigo-800',
  'lulu': 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 border-rose-100 dark:border-rose-800',
  'academia.edu': 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-100 dark:border-green-800',
  'academiaedu': 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-green-100 dark:border-green-800',
  'box': 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border-blue-100 dark:border-blue-800',
};

const PLATFORM_ICONS: Record<string, string> = {
  'slideshare': 'ri-slideshow-line',
  'scribd': 'ri-book-open-line',
  'issuu': 'ri-newspaper-line',
  'mediafire': 'ri-download-cloud-line',
  '4shared': 'ri-folder-cloud-line',
  'lulu': 'ri-book-3-line',
  'academia.edu': 'ri-graduation-cap-line',
  'academiaedu': 'ri-graduation-cap-line',
  'box': 'ri-folder-cloud-line',
};

interface CitationScorecard {
  titleHasKeyword: boolean;
  filenameOk: boolean;
  hasBacklink: boolean;
  score: number;
  verdict: 'High Quality' | 'Needs Optimisation' | 'Poor Quality';
  filenameChecks: {
    isLowercase: boolean;
    isHyphenated: boolean;
    hasKeyword: boolean;
  };
  matchedKeyword: string | null;
  matchedFilenameKeyword: string | null;
}

function evaluateAsset(
  assetUrl: string, 
  title: string | undefined, 
  snippet: string | undefined, 
  availableKeywords: string[] | undefined, 
  brandUrl: string | undefined
): CitationScorecard {
  const keywords = (availableKeywords || []).map(kw => kw.trim().toLowerCase()).filter(Boolean);
  
  // 1. Check title keyword (matches ANY keyword)
  let titleHasKeyword = false;
  let matchedKeyword: string | null = null;
  const titleLower = title ? title.toLowerCase() : "";
  for (const kw of keywords) {
    if (titleLower.includes(kw)) {
      titleHasKeyword = true;
      const originalKw = availableKeywords?.find(k => k.trim().toLowerCase() === kw) || kw;
      matchedKeyword = originalKw;
      break;
    }
  }

  // 2. Check filename (slug) formatting (reversing path segments to find document slug first)
  let isLowercase = false;
  let isHyphenated = false;
  let hasKeyword = false;
  let matchedFilenameKeyword: string | null = null;
  try {
    const urlObj = new URL(assetUrl);
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    const slugSegment = [...pathSegments].reverse().find(seg => /[a-z]/i.test(seg)) || "";
    isLowercase = slugSegment === slugSegment.toLowerCase();
    isHyphenated = slugSegment.includes('-') && !slugSegment.includes('_');
    
    for (const kw of keywords) {
      const cleanKw = kw.replace(/\s+/g, '-');
      if (slugSegment.includes(cleanKw) || slugSegment.includes(kw)) {
        hasKeyword = true;
        const originalKw = availableKeywords?.find(k => k.trim().toLowerCase() === kw) || kw;
        matchedFilenameKeyword = originalKw;
        break;
      }
    }
  } catch (e) {
    // Fail-safe
  }
  const filenameOk = isLowercase && isHyphenated && hasKeyword;

  // 3. Check backlink presence in snippet
  let hasBacklink = false;
  if (brandUrl && snippet) {
    const cleanBrandDomain = brandUrl
      .replace(/https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split('/')[0]
      .toLowerCase();
    hasBacklink = snippet.toLowerCase().includes(cleanBrandDomain);
  }

  // Calculate Score (out of 100) - 5-point check system (20% each)
  const checks = [titleHasKeyword, isLowercase, isHyphenated, hasKeyword, hasBacklink];
  const passedChecks = checks.filter(Boolean).length;
  const score = Math.round((passedChecks / checks.length) * 100);

  // Determine Verdict
  let verdict: 'High Quality' | 'Needs Optimisation' | 'Poor Quality' = 'Poor Quality';
  if (score >= 80) verdict = 'High Quality';
  else if (score >= 40) verdict = 'Needs Optimisation';

  return { 
    titleHasKeyword, 
    filenameOk, 
    hasBacklink, 
    score, 
    verdict,
    filenameChecks: {
      isLowercase,
      isHyphenated,
      hasKeyword
    },
    matchedKeyword,
    matchedFilenameKeyword
  };
}

export default function PDFSubmissionPage({ onBack, darkMode, userId }: PDFSubmissionPageProps) {
  usePageTracking({ moduleName: 'SEO Manager', submoduleName: 'Implementation / PDF Submission', routePath: '/dashboard#pdf-submission' });

  const [websiteState, setWebsiteState] = useState<'new_website' | 'existing_website'>('new_website');
  const [domainAgeLoading, setDomainAgeLoading] = useState(true);
  const [statusData, setStatusData] = useState<PDFStatusData | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PDFGenerationResult | null>(null);
  const [generatedPdfId, setGeneratedPdfId] = useState<number | null>(null);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [markCompleteSuccess, setMarkCompleteSuccess] = useState(false);
  const [taskId] = useState(() => crypto.randomUUID());

  const [checkedPlatforms, setCheckedPlatforms] = useState<Record<string, boolean>>({});
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);
  const [platforms, setPlatforms] = useState<any[]>([]);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Edit Metadata State
  const [isEditingMeta, setIsEditingMeta] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editTags, setEditTags] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  // Dynamic Website Age Lock
  const [isNewWebsite, setIsNewWebsite] = useState<boolean | null>(null);

  // Discovery Audit State
  const [discoveredAssets, setDiscoveredAssets] = useState<any[]>([]);
  const [isAuditing, setIsAuditing] = useState(false);
  const [auditScanId, setAuditScanId] = useState<string | null>(null);
  const [auditCompleted, setAuditCompleted] = useState(false);
  const [expandedAssetId, setExpandedAssetId] = useState<number | null>(null);

  // Library/Gallery state
  const [libraryListings, setLibraryListings] = useState<any[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [hasDefaultSelected, setHasDefaultSelected] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Domain age auto-detection and static platform fetch
  useEffect(() => {
    getPdfSubmissionPlatforms().then(res => {
      if (res.platforms) setPlatforms(res.platforms);
    }).catch(() => {});
    
    const websiteUrl = localStorage.getItem('website_url');
    if (!websiteUrl) { setDomainAgeLoading(false); return; }
    checkDomainAge(websiteUrl)
      .then((res) => {
        setIsNewWebsite(res.is_new_website);
        setWebsiteState(res.is_new_website ? 'new_website' : 'existing_website');
      })
      .catch(() => {})
      .finally(() => setDomainAgeLoading(false));
  }, []);

  // Poll for audit results
  useEffect(() => {
    if (!auditScanId || !isAuditing) return;
    const interval = setInterval(async () => {
      try {
        const statusData = await getPdfSubmissionScanStatus(auditScanId);
        if (statusData.status === 'COMPLETED') {
          if (statusData.assets) {
            setDiscoveredAssets(statusData.assets);
          }
          setIsAuditing(false);
          setAuditCompleted(true);
        } else if (statusData.status === 'FAILED') {
          setIsAuditing(false);
          setAuditCompleted(true);
          console.error('Audit failed:', statusData.error_detail);
        }
      } catch (err) {
        console.error('Error polling audit status:', err);
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [auditScanId, isAuditing]);

  const handleAudit = async () => {
    setIsAuditing(true);
    setAuditCompleted(false);
    setDiscoveredAssets([]);
    try {
      if (!userId) {
        console.error('No user ID found for audit');
        setIsAuditing(false);
        return;
      }
      const res = await initiatePdfSubmissionScan(userId);
      setAuditScanId(res.scan_id);
    } catch (err) {
      console.error('Error initiating audit:', err);
      setIsAuditing(false);
    }
  };

  // Fetch PDF Library from backend
  const fetchLibrary = async () => {
    if (!userId) return;
    setLibraryLoading(true);
    try {
      const res = await getPDFLibrary();
      if (res.status === 'success') {
        setLibraryListings(res.library);
      }
    } catch (e) {
      console.error('Error fetching PDF library:', e);
    } finally {
      setLibraryLoading(false);
    }
  };

  // Fetch library when userId changes
  useEffect(() => {
    fetchLibrary();
  }, [userId]);

  // Default selection logic: select the newest PDF
  useEffect(() => {
    if (libraryListings.length > 0 && !hasDefaultSelected) {
      const newestPdf = libraryListings[0];
      if (newestPdf) {
        setResult({
          id: newestPdf.id,
          pdf_url: newestPdf.pdf_url,
          metadata_pack: {
            title: newestPdf.title || '',
            description: newestPdf.description || '',
            tags: newestPdf.tags || '',
          },
          checklist: newestPdf.published_platforms || []
        });
        setGeneratedPdfId(newestPdf.id);
        setHasDefaultSelected(true);
      }
    } else if (libraryListings.length === 0 && !libraryLoading) {
      setResult(null);
      setGeneratedPdfId(null);
    }
  }, [libraryListings, hasDefaultSelected, libraryLoading]);

  // Sync checked platforms with the selected PDF checklist
  useEffect(() => {
    if (result) {
      const initialChecked: Record<string, boolean> = {};
      (result.checklist || []).forEach((platformName: string) => {
        initialChecked[platformName] = true;
      });
      setCheckedPlatforms(initialChecked);
    } else {
      setCheckedPlatforms({});
    }
  }, [result]);

  // Load prerequisites / audit data whenever websiteState changes
  useEffect(() => {
    if (!userId) return;
    setStatusLoading(true);
    getPDFStatus(userId, websiteState)
      .then(res => {
        setStatusData(res.data);
        if (res.data.available_keywords && res.data.available_keywords.length > 0) {
          setSelectedKeyword(res.data.primary_keyword || res.data.available_keywords[0]);
        }
      })
      .catch(() => setStatusData(null))
      .finally(() => setStatusLoading(false));
  }, [userId, websiteState]);

  const handleTogglePlatform = (platformName: string) => {
    setCheckedPlatforms(prev => {
      const updated = { ...prev, [platformName]: !prev[platformName] };
      return updated;
    });
  };

  const handleGenerate = async () => {
    setError(null);
    setGenerating(true);
    setResult(null);
    setGeneratedPdfId(null);
    setHasDefaultSelected(false);
    setCheckedPlatforms({});
    try {
      const res = await generatePDF({ user_id: userId, website_state: websiteState, primary_keyword: selectedKeyword || undefined });
      setResult(res);
      if (res.id) {
        setGeneratedPdfId(res.id);
      }
      await fetchLibrary();
    } catch (e: any) {
      setError(e?.message || 'PDF generation failed. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!generatedPdfId) {
      setError('No generated PDF found to mark as complete.');
      return;
    }
    setIsMarkingComplete(true);
    setError(null);
    try {
      const selectedPlatforms = Object.keys(checkedPlatforms).filter(p => checkedPlatforms[p]);
      await completeSubmission(generatedPdfId, { platforms: selectedPlatforms });
      setMarkCompleteSuccess(true);
      setTimeout(() => setMarkCompleteSuccess(false), 3000);
      await fetchLibrary();
    } catch (e: any) {
      setError(e?.message || 'Failed to update complete status. Please try again.');
    } finally {
      setIsMarkingComplete(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const keyword = result.metadata_pack.title.split(' ').slice(0, 3).join('-').toLowerCase();
    const filename = `${keyword}-seo-guide.pdf`;
    downloadPDFFromS3(result.pdf_url, filename);
  };

  const handleSaveMetadata = async () => {
    if (!result || !result.id) return;
    setSavingMeta(true);
    setError(null);
    try {
      const res = await updatePDFMetadata(result.id, {
        title: editTitle,
        description: editDesc,
        tags: editTags
      });
      setResult({
        ...result,
        metadata_pack: {
          title: res.title,
          description: res.description,
          tags: res.tags
        }
      });
      setIsEditingMeta(false);
      await fetchLibrary();
    } catch (e: any) {
      setError(e?.message || 'Failed to update metadata.');
    } finally {
      setSavingMeta(false);
    }
  };



  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const newSteps = [
    { icon: 'ri-magic-line', title: 'AI Content Generation', desc: 'AI writes a 500+ word branded SEO guide with embedded backlinks.' },
    { icon: 'ri-file-pdf-2-line', title: 'Branded PDF Compilation', desc: 'ReportLab renders a branded PDF using your logo, primary & secondary colors.' },
    { icon: 'ri-cloud-upload-line', title: 'Secure Storage', desc: 'The PDF is securely saved and prepared for distribution.' },
    { icon: 'ri-share-line', title: 'Manual Submission', desc: 'Submit to high-DA document sites to earn valuable authority backlinks.' },
    { icon: 'ri-link-m', title: 'Track Live URLs', desc: 'Paste and save each platform live URL to track backlink health.' },
  ];

  const existingSteps = [
    { icon: 'ri-search-eye-line', title: 'Audit Submissions', desc: 'Review all previously tracked PDF submissions and their live status.' },
    { icon: 'ri-refresh-line', title: 'Refresh PDF', desc: 'Regenerate with updated 2026 industry keywords and current brand colors.' },
    { icon: 'ri-upload-cloud-2-line', title: 'Resubmit to Platforms', desc: 'Upload the refreshed PDF to document-sharing platforms for updated backlinks.' },
  ];

  const steps = websiteState === 'new_website' ? newSteps : existingSteps;

  return (
    <div className={`space-y-6 pb-24 ${darkMode ? 'dark text-white' : 'text-gray-900'}`}>
      {/* Header */}
      <div>
        <button onClick={onBack} className="flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors mb-3 cursor-pointer border-none bg-transparent">
          <i className="ri-arrow-left-line mr-1.5" /> Back to Off-Page Content
        </button>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <i className="ri-file-pdf-2-line text-blue-600" /> PDF Submission
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Generate branded AI PDFs and submit to high-DA document platforms for backlinks.</p>
          </div>

          {/* Website State Toggle */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1 border border-gray-200 dark:border-gray-700">
            {domainAgeLoading ? (
              <span className="px-4 py-2 text-xs text-gray-400 flex items-center gap-1.5"><i className="ri-loader-4-line animate-spin" /> Detecting…</span>
            ) : (
              <>
                <button
                  onClick={() => setWebsiteState('new_website')}
                  disabled={isNewWebsite === false}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center border-none ${
                    isNewWebsite === false ? 'opacity-50 cursor-not-allowed bg-transparent' : 'cursor-pointer'
                  } ${
                    websiteState === 'new_website'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-transparent'
                  }`}
                >
                  New Website
                  {isNewWebsite === false && <i className="ri-lock-line ml-1.5"></i>}
                </button>
                <button
                  onClick={() => setWebsiteState('existing_website')}
                  disabled={isNewWebsite === true}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center border-none ${
                    isNewWebsite === true ? 'opacity-50 cursor-not-allowed bg-transparent' : 'cursor-pointer'
                  } ${
                    websiteState === 'existing_website'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 bg-transparent'
                  }`}
                >
                  Existing Website
                  {isNewWebsite === true && <i className="ri-lock-line ml-1.5"></i>}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Brand Prerequisites Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-building-2-line text-blue-500 text-lg" />
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Brand Prerequisites (Auto-filled from Onboarding)
          </h3>
        </div>

        {statusLoading || !statusData ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/3 mb-3"></div>
                <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: 'Brand Name', value: statusData.brand_name, icon: 'ri-store-2-line' },
              { label: 'Website URL', value: statusData.website_url, icon: 'ri-global-line' },
              { label: 'Description', value: statusData.business_description, icon: 'ri-file-text-line' },
            ].map(item => (
              <div key={item.label} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
                <div className="flex items-center gap-1.5 mb-1">
                  <i className={`${item.icon} text-blue-500 text-sm`} />
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{item.label}</span>
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.value || '—'}</p>
              </div>
            ))}
            {/* Colors */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-1.5 mb-2">
                <i className="ri-palette-line text-blue-500 text-sm" />
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Brand Colors</span>
              </div>
              <div className="flex gap-2">
                {statusData.primary_color && <div className="w-8 h-8 rounded-lg border-2 border-white shadow-sm" style={{ backgroundColor: statusData.primary_color }} title={statusData.primary_color} />}
                {statusData.secondary_color && <div className="w-8 h-8 rounded-lg border-2 border-white shadow-sm" style={{ backgroundColor: statusData.secondary_color }} title={statusData.secondary_color} />}
              </div>
            </div>
            {/* Keywords Selection */}
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 lg:col-span-2 border border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                  <i className="ri-key-2-line text-blue-500 text-sm" />
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Select Primary Keyword</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {(statusData.available_keywords || []).map(kw => {
                  const isSel = selectedKeyword === kw;
                  return (
                    <button
                      key={kw}
                      onClick={() => setSelectedKeyword(kw)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer transition-all ${
                        isSel
                          ? 'bg-blue-50 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300 shadow-sm'
                          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`}
                    >
                      <i className={`ri-${isSel ? 'radio-button-fill' : 'checkbox-blank-circle-line'} mr-1`}></i>
                      {kw}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ─── Audit Existing PDF Submissions ─── */}
      {websiteState === 'existing_website' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <i className="ri-radar-line text-blue-600 dark:text-blue-400"></i>
                Audit Existing PDF Submissions
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use our crawler engine to discover and audit existing PDF submissions for your brand across the web.
              </p>
            </div>
            <button
              onClick={handleAudit}
              disabled={isAuditing}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2 border-none cursor-pointer ${
                isAuditing 
                  ? 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm'
              }`}
            >
              {isAuditing ? (
                <><i className="ri-loader-4-line animate-spin"></i> Auditing...</>
              ) : (
                <><i className="ri-search-eye-line"></i> Run Discovery Audit</>
              )}
            </button>
          </div>

          {isAuditing && (
            <div className="py-8 flex flex-col items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Crawling the web for existing PDF submissions...</p>
            </div>
          )}

          {auditCompleted && discoveredAssets.length === 0 && (
            <div className="py-6 text-center border-t border-gray-100 dark:border-gray-700 mt-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-50 dark:bg-gray-800 mb-3">
                <i className="ri-search-line text-xl text-gray-400"></i>
              </div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">No existing PDF submissions found</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
                No existing PDF submissions found. Our crawler couldn't find any PDF documents published for your website.
              </p>
            </div>
          )}

          {auditCompleted && discoveredAssets.length > 0 && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-left border-collapse min-w-[700px]">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300 w-10"></th>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Platform</th>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Asset URL</th>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Live Link</th>
                    <th className="py-3 px-4 text-xs font-semibold text-gray-600 dark:text-gray-300">Citation Quality</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {discoveredAssets.map((asset) => {
                    const isExpanded = expandedAssetId === asset.id;
                    const evaluation = evaluateAsset(
                      asset.asset_url,
                      asset.asset_title,
                      asset.asset_snippet,
                      statusData?.available_keywords,
                      statusData?.website_url
                    );

                    return (
                      <React.Fragment key={asset.id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="py-3 px-4">
                            <button
                              onClick={() => setExpandedAssetId(isExpanded ? null : asset.id)}
                              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 border-none bg-transparent cursor-pointer flex items-center justify-center transition-all"
                              title={isExpanded ? "Collapse Scorecard" : "View Scorecard"}
                            >
                              <i className={`ri-arrow-${isExpanded ? 'down' : 'right'}-s-line text-lg`} />
                            </button>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                                <i className="ri-global-line text-gray-500 dark:text-gray-400 text-sm"></i>
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {asset.platform_domain || asset.platform_name || 'Unknown'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <a
                              href={asset.asset_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 dark:text-blue-400 font-mono break-all hover:underline flex items-center gap-1"
                            >
                              {asset.asset_url} <i className="ri-external-link-line text-[10px]"></i>
                            </a>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${
                              asset.status === 'ACTIVE'
                                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                                : 'bg-gray-55 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700'
                            }`}>
                              {asset.status === 'ACTIVE' ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full border ${
                              evaluation.verdict === 'High Quality'
                                ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800'
                                : evaluation.verdict === 'Needs Optimisation'
                                ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800'
                                : 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800'
                            }`}>
                              {evaluation.verdict} ({evaluation.score}%)
                            </span>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={5} className="bg-gray-50/50 dark:bg-gray-900/30 p-4 border-t border-b border-gray-100 dark:border-gray-800">
                              <div className="max-w-3xl mx-auto space-y-4">
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-2">
                                  <h4 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
                                    <i className="ri-survey-line text-blue-600 dark:text-blue-400"></i>
                                    SEO Citation Scorecard
                                  </h4>
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Score: <strong className="text-gray-900 dark:text-white">{evaluation.score}%</strong>
                                  </span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* Check 1: Keyword in Title */}
                                  <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700/80 shadow-sm flex flex-col justify-between">
                                    <div>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Title Keyword</span>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                                          evaluation.titleHasKeyword
                                            ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400'
                                            : 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400'
                                        }`}>
                                          {evaluation.titleHasKeyword ? 'PASSED' : 'FAILED'}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-600 dark:text-gray-300 leading-normal">
                                        {evaluation.titleHasKeyword
                                          ? `Matched keyword "${evaluation.matchedKeyword}" in the document title.`
                                          : `Document title does not contain any of your primary keywords.`}
                                      </p>
                                    </div>
                                    <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-2 text-[10px] text-gray-400 dark:text-gray-500 truncate" title={asset.asset_title}>
                                      Title: {asset.asset_title || 'No Title Found'}
                                    </div>
                                  </div>

                                  {/* Check 2: Filename Naming Standard */}
                                  <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700/80 shadow-sm flex flex-col justify-between">
                                    <div>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Filename Slug</span>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                                          evaluation.filenameOk
                                            ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400'
                                            : 'bg-amber-50 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400'
                                        }`}>
                                          {evaluation.filenameOk ? 'PASSED' : 'WARN'}
                                        </span>
                                      </div>
                                      <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 mt-1 pl-0 list-none">
                                        <li className="flex items-center gap-1.5">
                                          <i className={evaluation.filenameChecks.isLowercase ? "ri-checkbox-circle-fill text-emerald-500" : "ri-close-circle-fill text-red-500"} />
                                          <span>All lowercase</span>
                                        </li>
                                        <li className="flex items-center gap-1.5">
                                          <i className={evaluation.filenameChecks.isHyphenated ? "ri-checkbox-circle-fill text-emerald-500" : "ri-close-circle-fill text-red-500"} />
                                          <span>Hyphenated, no spaces</span>
                                        </li>
                                        <li className="flex items-center gap-1.5">
                                          <i className={evaluation.filenameChecks.hasKeyword ? "ri-checkbox-circle-fill text-emerald-500" : "ri-close-circle-fill text-red-500"} />
                                          <span>
                                            {evaluation.filenameChecks.hasKeyword && evaluation.matchedFilenameKeyword
                                              ? `Contains "${evaluation.matchedFilenameKeyword}"`
                                              : 'Contains target keyword'}
                                          </span>
                                        </li>
                                      </ul>
                                    </div>
                                    <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-2 text-[10px] text-gray-400 dark:text-gray-500 truncate" title={asset.asset_url}>
                                      URL: {asset.asset_url}
                                    </div>
                                  </div>

                                  {/* Check 3: Brand Citation & Backlink */}
                                  <div className="bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700/80 shadow-sm flex flex-col justify-between">
                                    <div>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Brand Citation</span>
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${
                                          evaluation.hasBacklink
                                            ? 'bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-400'
                                            : 'bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-400'
                                        }`}>
                                          {evaluation.hasBacklink ? 'FOUND' : 'MISSING'}
                                        </span>
                                      </div>
                                      <p className="text-xs text-gray-600 dark:text-gray-300 leading-normal">
                                        {evaluation.hasBacklink
                                          ? `Brand domain "${statusData?.website_url ? statusData.website_url.replace(/https?:\/\/(www\.)?/, "") : ""}" detected in the document snippet.`
                                          : `Could not identify brand domain link "${statusData?.website_url ? statusData.website_url.replace(/https?:\/\/(www\.)?/, "") : ""}" in the crawled page snippet.`}
                                      </p>
                                    </div>
                                    <div className="border-t border-gray-100 dark:border-gray-700 mt-2 pt-2 text-[10px] text-gray-400 dark:text-gray-500 italic truncate" title={asset.asset_snippet || ''}>
                                      "{asset.asset_snippet || 'No snippet preview available'}"
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Submission Audit Report Card (Commented out)
      {(!statusLoading && statusData && (statusData.total_tracked ?? 0) > 0) && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <i className="ri-file-list-3-line text-purple-500 text-lg" />
            <h3 className="font-semibold text-gray-900 dark:text-white">
              Submission Audit Report
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {statusData.total_tracked ?? 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Total Tracked</div>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {statusData.healthy_links ?? 0}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Healthy Links</div>
            </div>
            <div className={`${statusData.action_required ? 'bg-orange-50 dark:bg-orange-900/20' : 'bg-gray-50 dark:bg-gray-700/50'} rounded-xl p-4 text-center transition-colors`}>
              <div className={`text-2xl font-bold ${statusData.action_required ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'}`}>
                {statusData.action_required ? '!' : '✓'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{statusData.action_required ? 'Action Required' : 'All Good'}</div>
            </div>
          </div>
        </div>
      )}      {/* Target Platforms Overview (Only shown before generation) */}
      {!result && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <i className="ri-global-line text-blue-600" /> Target Platforms Overview
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {platforms.map(p => (
              <div key={p.name} className="bg-gray-55 dark:bg-gray-700/50 rounded-xl p-3 text-center border border-gray-100 dark:border-gray-800">
                <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mx-auto mb-2 ${PLATFORM_COLORS[p.name.toLowerCase()] || 'bg-gray-100 dark:bg-gray-800'}`}>
                  <i className={`${PLATFORM_ICONS[p.name.toLowerCase()] || 'ri-global-line'} text-xl`} />
                </div>
                <div className="text-xs font-semibold text-gray-900 dark:text-white capitalize">{p.display}</div>
                <div className="text-xs text-gray-400 mt-0.5">DA {p.da}</div>
              </div>
            ))}
          </div>
        </div>
      )}


      {/* Error display */}
      {error && (
        <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <i className="ri-error-warning-line text-red-500 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Generate button (Full-width, blue styled) */}
      <button
        onClick={handleGenerate}
        disabled={generating}
        id="generate-pdf-btn"
        className="w-full py-4 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-base flex items-center justify-center gap-3 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-200/20 border-none cursor-pointer"
      >
        <i className="ri-file-pdf-2-line text-xl" />
        {generating ? 'Generating PDF Guide…' : result ? 'Regenerate PDF Guide' : 'Generate PDF Guide'}
      </button>

      {/* Generated PDF Guides Gallery Section */}
      {libraryListings.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm mt-6">
          <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
            <i className="ri-folder-open-line text-blue-600" /> Generated PDF Guides Library
          </h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
            Click a PDF guide to view its details, edit its SEO metadata, and track platform submission checklist status below.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {libraryListings.map((pdf) => {
              const isSelected = generatedPdfId === pdf.id;
              const dateStr = pdf.created_at ? new Date(pdf.created_at).toLocaleDateString() : '';
              
              return (
                <div
                  key={pdf.id}
                  onClick={() => {
                    setResult({
                      id: pdf.id,
                      pdf_url: pdf.pdf_url,
                      metadata_pack: {
                        title: pdf.title || '',
                        description: pdf.description || '',
                        tags: pdf.tags || '',
                      },
                      checklist: pdf.published_platforms || []
                    });
                    setGeneratedPdfId(pdf.id);
                  }}
                  className={`group relative cursor-pointer rounded-xl overflow-hidden border transition-all bg-gray-55 dark:bg-gray-900/50 hover:shadow-md ${
                    isSelected
                      ? 'border-blue-500 ring-2 ring-blue-100 dark:ring-blue-900/30'
                      : 'border-gray-200 dark:border-gray-700 hover:border-blue-400'
                  }`}
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-red-500/10 to-orange-500/10 dark:from-red-950/20 dark:to-orange-950/20 relative overflow-hidden flex items-center justify-center border-b border-gray-100 dark:border-gray-800">
                    {pdf.image_url ? (
                      <img
                        src={pdf.image_url}
                        alt={pdf.title || 'Branded SEO Guide'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <i className="ri-file-pdf-2-line text-4xl text-red-500 group-hover:scale-110 transition-transform duration-300" />
                    )}
                    {pdf.published_platforms && pdf.published_platforms.length > 0 && (
                      <span className="absolute top-2 right-2 px-1.5 py-0.5 bg-emerald-500 text-white rounded text-[8px] font-bold uppercase flex items-center gap-0.5 z-10">
                        <i className="ri-check-line" /> {pdf.published_platforms.length} Platforms
                      </span>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-xs font-bold text-gray-900 dark:text-white truncate" title={pdf.title || 'Branded SEO Guide'}>
                      {pdf.title || 'Branded SEO Guide'}
                    </p>
                    <div className="flex items-center justify-between mt-1 text-[10px] text-gray-550 dark:text-gray-400">
                      <span className="truncate">{dateStr}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Consolidated Results Grid (No modal - inline!) */}
      {result && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mt-6">
          {/* Left Column: Document Card & SEO Metadata */}
          <div className="lg:col-span-5 space-y-6">
            {/* Branded PDF Guide Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center border border-blue-100 dark:border-blue-900/50">
                  <i className="ri-file-pdf-2-line text-blue-600 text-2xl" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-sm truncate max-w-[200px]" title={result.metadata_pack.title}>
                    {result.metadata_pack.title || 'Branded SEO Guide'}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">PDF Guide • 500+ Words • Branded Format</p>
                </div>
              </div>

              <div className="space-y-2">
                <button
                  onClick={handleDownload}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer border-none shadow-sm hover:shadow"
                >
                  <i className="ri-download-cloud-2-line text-lg" /> Download PDF Guide
                </button>

                <button
                  onClick={() => setShowPreview(true)}
                  className="w-full py-3 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 text-blue-650 dark:text-blue-400 border border-blue-200 dark:border-blue-900/50 font-semibold rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm hover:shadow"
                >
                  <i className="ri-eye-line text-lg" /> Preview PDF Guide
                </button>
              </div>
            </div>

            {/* SEO Metadata Pack */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-price-tag-3-line text-blue-600 text-lg" />
                  <h3 className="font-semibold text-gray-900 dark:text-white text-sm">SEO Metadata Pack</h3>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Title', value: result.metadata_pack.title, icon: 'ri-heading', field: 'title' },
                  { label: 'Description', value: result.metadata_pack.description, icon: 'ri-file-text-line', field: 'desc' },
                  { label: 'Tags / Keywords', value: result.metadata_pack.tags, icon: 'ri-price-tag-3-line', field: 'tags' },
                ].map(m => (
                  <div key={m.label} className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-3 border border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        <i className={`${m.icon} text-blue-600`} /> {m.label}
                      </div>
                      <button
                        onClick={() => handleCopy(m.value, m.field)}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 cursor-pointer bg-transparent border-none font-semibold"
                      >
                        {copiedField === m.field ? (
                          <><i className="ri-check-line text-emerald-500" /> Copied!</>
                        ) : (
                          <><i className="ri-file-copy-line" /> Copy</>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed font-medium">{m.value}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Platform Submission Checklist */}
          <div className="lg:col-span-7 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base">Suggested Platforms — Submission Checklist</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Submit your generated PDF manually to these platforms. Check them off once done.</p>
              </div>
              <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                <button
                  onClick={handleMarkComplete}
                  disabled={isMarkingComplete || !Object.values(checkedPlatforms).some(Boolean)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-200 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 disabled:cursor-not-allowed text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 cursor-pointer border-none shadow-sm hover:shadow"
                >
                  {isMarkingComplete ? (
                    <><i className="ri-loader-4-line animate-spin" /> Updating...</>
                  ) : (
                    <><i className="ri-checkbox-circle-line text-sm" /> Mark as Complete</>
                  )}
                </button>
                {markCompleteSuccess && (
                  <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1 animate-fade-in">
                    <i className="ri-checkbox-circle-fill" /> Saved!
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-4 mt-6">
              {platforms.map(p => {
                const isChecked = !!checkedPlatforms[p.name];

                return (
                  <div 
                    key={p.name} 
                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${isChecked ? 'bg-emerald-50/30 dark:bg-emerald-950/10 border-emerald-100 dark:border-emerald-900/50' : 'bg-gray-55 dark:bg-gray-900/30 border-gray-100 dark:border-gray-800'}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleTogglePlatform(p.name)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
                      />
                      <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${PLATFORM_COLORS[p.name.toLowerCase()] || 'bg-gray-100 dark:bg-gray-800'}`}>
                        <i className={`${PLATFORM_ICONS[p.name.toLowerCase()] || 'ri-global-line'} text-sm`} />
                      </div>
                      <div>
                        <span className={`text-sm font-semibold text-gray-900 dark:text-white capitalize ${isChecked ? 'line-through text-gray-400 dark:text-gray-500 font-medium' : ''}`}>{p.display}</span>
                        <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">DA {p.da}</span>
                      </div>
                    </div>
                    
                    <a
                      href={`https://${p.url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold"
                    >
                      Visit Site <i className="ri-external-link-line" />
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {showPreview && result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4" onClick={() => setShowPreview(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-4xl w-full h-[85vh] flex flex-col shadow-2xl relative border border-gray-200 dark:border-gray-700 animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div>
                <h3 className="font-bold text-base text-gray-900 dark:text-white truncate max-w-[350px] sm:max-w-[500px]" title={result.metadata_pack.title}>
                  {result.metadata_pack.title || 'Branded SEO Guide'}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">PDF Document Preview</p>
              </div>
              <button onClick={() => setShowPreview(false)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 flex items-center justify-center text-gray-500 hover:text-gray-850 dark:text-gray-400 dark:hover:text-white border-none cursor-pointer">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="flex-1 bg-gray-100 dark:bg-gray-900 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-750 relative">
              <iframe
                src={`${result.pdf_url}#toolbar=0`}
                className="w-full h-full border-none"
                title="PDF Preview"
              />
            </div>
            <div className="flex justify-end gap-3 mt-4 flex-shrink-0">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-250 border-none font-semibold rounded-xl cursor-pointer"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDownload();
                  setShowPreview(false);
                }}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl cursor-pointer border-none shadow-sm flex items-center gap-1.5"
              >
                <i className="ri-download-cloud-2-line text-sm" /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

